import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDownloadOrderInvoice, getOrders, placeOrder } from "../../State/Order/Action";
import { DataGrid } from "@mui/x-data-grid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Button, Card, CardHeader, Box, CardContent,
  TextField,
  Tooltip,
  Paper,
  Modal,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Close, FileDownload, PictureAsPdf, Print, Visibility } from "@mui/icons-material";
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import dayjs from "dayjs";
import ImagePopup from "../../../Common/ImagePopup";
import defaultImage from "../../../Common/productDefaultImage.png"

const OrdersWithCustomSize = () => {
  const dispatch = useDispatch();
  const { vendorOrder } = useSelector((store) => store);
  const [selectedOrderRow, setSelectedOrderRow] = useState(null);
  const [acceptPopupOpen, setAcceptPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDateHistoryModal, setOpenDateHistoryModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // Add this state for selection
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [customSizes, setCustomSizes] = useState([]);
  const [open, setOpen] = useState(false);

  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  useEffect(() => {
    dispatch(getOrders());
  }, [dispatch]);

  const handleAcceptOrder = (orderId) => {
    if (orderId) {
      dispatch(placeOrder(orderId));
      setAcceptPopupOpen(false)
    }
  };

  const downloadOrderInvoice = (orderId, uniqueOrderId) => {
    if (orderId) {
      dispatch(getDownloadOrderInvoice(orderId, uniqueOrderId));
    }
  }

  // Flatten orders data for DataGrid
  const rows =
    vendorOrder.orders?.response
      ?.filter(o => o.customSizes?.length > 0) // सिर्फ custom size वाले orders
      ?.map((order, index) => ({
        id: `${order.id}-${order?.orderItems?.map((item) => item?.product?.id).join("-")}-${index}`, // Unique ID for DataGrid
        orderId: order.id,
        uniqueOrderId: order.orderId,
        orderDate: order.orderDate ? dayjs(order.orderDate).format("DD MMM YYYY HH:mm") : "----",
        employeeName: order.employee?.name || "----",
        employeeEmail: order.employee?.email || "----",
        employeePhone: order.employee?.phone || "----",
        productTitle: order?.orderItems?.map((item) => item?.product?.title).join(", ") || "----",
        productImages: order?.orderItems
          ?.flatMap((item) =>
            (item?.product?.images || [])
              .filter((img) => img.isPrimary)
              .map((img) => img.fullUrl)
          )
          ?.filter(Boolean)
          ?.join(",") || "----",
        productName: order?.orderItems?.map((item) => item?.product?.productCategory?.name).join(", ") || "----",
        quantity: order?.orderItems?.map((item) => item?.quantity).join(", ") || 0,
        vendorPrice: order?.orderItems?.map((item) => item?.variant?.vendorPrice?.toFixed(2)).join(", ") || 0,
        color: order?.orderItems?.map((item) => item?.color).join(", ") || "----",
        size: order?.orderItems?.map((item) => item?.size).join(", ") || "----",
        customSizes: order?.customSizes,
        totalQuantity: order.totalItem || 0,
        totalPrice: order?.orderItems?.reduce(
          (sum, item) => sum + ((item?.variant?.vendorPrice || 0) * (item?.quantity || 0)),
          0
        ).toFixed(2),
        cancelReturnDate: order.cancelDate ? dayjs(order.cancelDate).format("DD MMM YYYY") : order.returnDate ? dayjs(order.returnDate).format("DD MMM YYYY") : "----",
        cancelReturnReason: order.cancelRegion || order?.returnReason || "----",
        returnImages: order?.imageUrls,
        deliveryDate: order.deliveryDate ? dayjs(order.deliveryDate).format("DD MMM YYYY") : "----",
        dispatchDate: order.dispatchDate ? dayjs(order.dispatchDate).format("DD MMM YYYY") : "----",
        orderStatus: order.orderStatus === "PLACED" ? "ORDER ACCEPTED" : order.orderStatus,
        employee: order?.employee ? "Accepted" : "Not Accept",
        fullOrder: order,
        penaltyPrice: order.penaltyPrice?.toFixed(2) || 0,
        deliveryDateHistories: order.deliveryDateHistories,
      })
      ) || [];

  // Filtered rows for search functionality
  const filteredRows = rows.filter((row) => {
    const query = searchQuery.toLowerCase();
    return (
      row.uniqueOrderId.toLowerCase().includes(query) ||
      row.orderDate.toLowerCase().includes(query) ||
      row.dispatchDate.toLowerCase().includes(query) ||
      row.employeeName.toLowerCase().includes(query) ||
      row.employeeEmail.toLowerCase().includes(query) ||
      row.productTitle.toLowerCase().includes(query) ||
      row.productName.toLowerCase().includes(query) ||
      row.orderStatus.toLowerCase().includes(query)
    );
  });

  const getExportData = () => {
    // If some rows are selected, filter them; else use all filteredRows
    if (selectedRowIds.length > 0) {
      return filteredRows.filter((row) => selectedRowIds.includes(row.id));
    }
    return filteredRows;
  };


  // Export, PDF, and Print Handlers
  const handleExportCSV = () => {
    const exportRows = getExportData(); // ✅ Call the function
    const csvContent = exportRows.map((row, index) => {
      return [
        index + 1,
        row.uniqueOrderId,
        row.orderDate,
        row.employeeName,
        row.employeeEmail,
        row.employeePhone,
        row.productTitle,
        row.productName,
        row.quantity,
        row.vendorPrice,
        row.color,
        row.size,
        row.totalQuantity,
        row.totalPrice,
        row.cancelDate,
        row.cancelRegion,
        row.deliveryDate,
        row.employee,
        row.orderStatus
      ].join(',');
    }).join('\n');

    const headers = [
      'S.No.',
      'Order ID',
      'Order Date',
      'Employee Name',
      'Employee Email',
      'Employee Phone',
      'Product Title',
      'Product Name',
      'Quantity',
      'Price',
      'Color',
      'Size',
      'Total Quantity',
      'Total Price',
      'Cancel Date',
      'Cancel Reason',
      'Delivery Date',
      'Employee Status',
      'Order Status',
    ].join(',');

    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'orders.csv';
    link.click();
  };

  const handleExportPDF = () => {
    const exportRows = getExportData(); // ✅ Call the function
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a2", // even larger than A3 to fit all columns
    });

    doc.setFontSize(18);
    doc.text("Order List", 40, 30);

    const tableColumn = [
      'S.No.',
      'Order ID',
      'Order Date',
      'Employee Name',
      'Employee Email',
      'Employee Phone',
      'Product Title',
      'Product Name',
      'Quantity',
      'Price',
      'Color',
      'Size',
      'Total Quantity',
      'Total Price',
      'Cancel Date',
      'Cancel Reason',
      'Delivery Date',
      'Employee Status',
      'Order Status',
    ];

    const tableRows = exportRows.map((row, index) => ([
      index + 1,
      row.uniqueOrderId,
      row.orderDate,
      row.employeeName,
      row.employeeEmail,
      row.employeePhone,
      row.productTitle,
      row.productName,
      row.quantity,
      row.vendorPrice,
      row.color,
      row.size,
      row.totalQuantity,
      row.totalPrice,
      row.cancelDate,
      row.cancelRegion,
      row.deliveryDate,
      row.employee,
      row.orderStatus
    ]));

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 50,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'top',
      },
      columnStyles: {
        1: { cellWidth: 100 },   // orderId
        6: { cellWidth: 100 },  // Product Titles
      },
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      bodyStyles: {
        halign: 'left'
      },
      margin: { top: 50, left: 10, right: 10, bottom: 20 },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 10, doc.internal.pageSize.height - 10);
      },
    });

    doc.save("orders.pdf");
  };

  // Define columns
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "uniqueOrderId", headerName: "Order ID", width: 120 },
    { field: "orderDate", headerName: "Order Date", width: 150, },
    { field: "dispatchDate", headerName: "Dispatch Date", width: 150, },
    { field: "employeeName", headerName: "Employee Name", width: 150 },
    { field: "employeeEmail", headerName: "Employee Email", width: 200 },
    { field: "employeePhone", headerName: "Employee Phone", width: 150 },
    { field: "productTitle", headerName: "Product Title", width: 150 },
    {
      field: "productImages",
      headerName: "Image(s)",
      width: 100,
      renderCell: (params) => {
        const images = params?.value
          ? params.value.split(",").map((url) => url.trim()) // multiple comma separated
          : [];

        if (images.length === 0) {
          return <span>----</span>;
        }

        return (
          <div style={{ display: "flex", gap: "5px" }}>
            {images.map((url, index) => (
              <img
                key={index}
                onClick={() => {
                  setImageOpen(true);
                  setImageUrl(url || defaultImage);
                }}
                src={url || defaultImage}
                alt={`aadhaar_image_${index}`}
                width={30}
                height={30}
                style={{ borderRadius: 8, objectFit: "cover", cursor: "pointer" }}
              />
            ))}
          </div>
        );
      },
    },
    { field: "productName", headerName: "Product Name", width: 120 },
    { field: "quantity", headerName: "Quantity", width: 100 },
    { field: "vendorPrice", headerName: "Price", width: 100 },
    { field: "color", headerName: "Color", width: 100 },
    {
      field: "size",
      headerName: "Size",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
          <Tooltip title="View Size Details">
            <IconButton
              size="small"
              sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
              onClick={() => {
                handleOpen();
                setCustomSizes(params.row.customSizes)
              }}
            >
              <Visibility fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: "totalQuantity", headerName: "Total Quantity", width: 100 },
    { field: "totalPrice", headerName: "Total Price", width: 100 },
    { field: "cancelReturnDate", headerName: "Cancel/Return Date", width: 100, },
    { field: "cancelReturnReason", headerName: "Cancel/Return Reason", width: 100 },
    {
      field: "returnImages",
      headerName: "Return Image(s)",
      width: 100,
      renderCell: (params) => {
        const images = params?.value

        if (images.length === 0) {
          return <span>----</span>;
        }

        return (
          <div style={{ display: "flex", gap: "5px" }}>
            {images.map((url, index) => (
              <img
                key={index}
                onClick={() => {
                  setImageOpen(true);
                  setImageUrl(url || defaultImage);
                }}
                src={url || defaultImage}
                alt={`aadhaar_image_${index}`}
                width={30}
                height={30}
                style={{ borderRadius: 8, objectFit: "cover", cursor: "pointer" }}
              />
            ))}
          </div>
        );
      },
    },
    { field: "deliveryDate", headerName: "Delivery Date", width: 120, },
    {
      field: "employee",
      headerName: "Employee Status",
      width: 150,
      renderCell: (params) => (
        <span
          className={` rounded-full px-3 uppercase py-2 font-bold ${params.value === "Accepted" ? "text-green-500" : "text-gray-500"
            }`}
        >
          {params.value}
        </span>
      )
    },
    {
      field: "orderStatus",
      headerName: "Order Status",
      width: 180,
      renderCell: (params) => {
        const status = params.value;

        // When status is PENDING, show Accept button
        if (status === "PENDING") {
          return (
            <Button
              variant="contained"
              onClick={() => {
                setSelectedOrderRow(params.row); // Pass the full row data
                setAcceptPopupOpen(true);
              }}
              sx={{
                background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: "6px",
                boxShadow: "0 2px 8px rgba(34,197,94,0.15)",
                px: 2,
                py: 0.4,
                transition: "all 0.2s",
                "&:hover": {
                  background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                  boxShadow: "0 4px 16px rgba(34,197,94,0.25)",
                  transform: "scale(1.02)",
                },
                minWidth: 'auto',
              }}
            >
              Accept Order
            </Button>
          );
        }

        // For other statuses, show a colored badge
        const statusColorMap = {
          DELIVERED: "bg-green-500",
          COMPLETED: "bg-green-400",
          SHIPPED: "bg-purple-500",
          CANCELLED: "bg-red-500",
          RETURNED: "bg-red-400",
          PICKED_UP: "bg-purple-400",
          "ORDER ACCEPTED": "bg-yellow-500",
        };

        const bgColor = statusColorMap[status] || "bg-gray-400";

        return (
          <div className="mt-1">
            <span className={`text-white rounded-full px-2 py-1  ${bgColor}`}>
              {status === "PLACED" ? "ORDER ACCEPTED" : status}
            </span>
          </div>
        );
      }
    },
    {
      field: "invoice",
      headerName: "Invoice",
      width: 140,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          disabled={params.row.orderStatus !== "PLACED"}
          onClick={() => downloadOrderInvoice(params.row.orderId, params.row.uniqueOrderId)}
          sx={{
            background: "#449AEF",
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.75rem",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(34,197,94,0.15)",
            px: 2,
            py: 0.4,
            transition: "all 0.2s",
            "&:hover": {
              background: "#157ce2ff)",
              boxShadow: "0 4px 16px rgba(34,197,94,0.25)",
              transform: "scale(1.02)",
            },
            minWidth: 'auto',
          }}
        >
          Download
        </Button>

      )
    },
    {
      field: "history",
      headerName: "History",
      width: 80,
      renderCell: (params) => (
        <Tooltip title="View History">
          <IconButton
            size="small"
            sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
            onClick={() => {
              setOpenDateHistoryModal(true);
              setSelectedOrder(params.row);
            }}
          >
            <Visibility fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
          </IconButton>
        </Tooltip>
      )
    }

  ];

  const excludedFields = ['serialNo', 'history']; // Add more fields if needed

  const exportableColumns = columns.filter(col => !excludedFields.includes(col.field));
  // Get headers from column definitions
  const headers = exportableColumns.map(col => col.headerName);
  const csvFields = exportableColumns.map(col => col.field);

  const handlePrint = () => {
    const exportRows = getExportData(); // ✅ Call the function
    const printWindow = window.open('', '_blank');
    const data = exportRows.map(row =>
      csvFields.map(field => row[field] ?? "----")
    );

    const html = `
      <html>
        <head>
          <title>Order List</title>
          <style>
            @media print {
              @page { size: A3 landscape; }
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              table-layout: auto;
            }
            th, td {
              border: 1px solid #999;
              padding: 4px;
              text-align: left;
              white-space: pre-wrap;
              word-break: break-word;
            }
            th {
             background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            tr:nth-child(even) {
              background: #f5f5f5;
            }
          </style>
        </head>
        <body>
          <h2>Order List</h2>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${data.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    };
  };


  return (
    <div >
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardHeader
          title={
            <Typography variant="h6" className="text-gray-700">
              ORDERS
            </Typography>
          }
        />
        <CardContent>
          {/* Buttons and Search Box */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Print
              </Button>
            </Box>
            {/* Search Box */}
            <Box>
              <TextField
                label="Search Orders"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: { xs: '100%', sm: '300px' } }}
              />
            </Box>
          </Box>
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
              getRowId={(row) => row.id}
              checkboxSelection           // ✅ Enable checkbox selection
              onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)} // Track selection
              selectionModel={selectedRowIds} // selected mode
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={vendorOrder.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap", // prevents wrapping
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </CardContent>

      </Card>

      <Dialog
        open={acceptPopupOpen}
        onClose={() => setAcceptPopupOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            bgcolor: "#ffffff",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.4rem",
            bgcolor: "#f3f4f6",
            borderBottom: "1px solid #e5e7eb",
            position: "relative",
          }}
        >
          Order Details
          <IconButton
            aria-label="close"
            onClick={() => setAcceptPopupOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 2, backgroundColor: "#f9fafb" }}>
          {selectedOrderRow && (
            <Grid container spacing={3}>
              {/* Product Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  📦 Product Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Title:</strong> {selectedOrderRow.productTitle}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Category:</strong> {selectedOrderRow.productName}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="body2"><strong>Color:</strong> {selectedOrderRow.color}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2"><strong>Size:</strong> {selectedOrderRow.size}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2"><strong>Quantity:</strong> {selectedOrderRow.quantity}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2"><strong>Price:</strong> ₹{selectedOrderRow.vendorPrice}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Divider */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderBottom: "1px dashed #d1d5db",
                    my: 1,
                  }}
                />
              </Grid>

              {/* Order Summary */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  📑 Order Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Typography variant="body2"><strong>Order ID:</strong> {selectedOrderRow.uniqueOrderId}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Order Date:</strong> {selectedOrderRow.orderDate}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Dispatch Date:</strong> {selectedOrderRow.dispatchDate}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Delivery Date:</strong> {selectedOrderRow.deliveryDate}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Total Quantity:</strong> {selectedOrderRow.totalQuantity}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Total Price:</strong> ₹{selectedOrderRow.totalPrice}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Status:</strong> {selectedOrderRow.orderStatus}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#f3f4f6",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setAcceptPopupOpen(false)}
            sx={{
              color: "#3b82f6",
              borderColor: "#3b82f6",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1,
              "&:hover": {
                backgroundColor: "#3b82f6",
                color: "#fff",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleAcceptOrder(selectedOrderRow.orderId)}
            sx={{
              background: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1,
              boxShadow: "0 4px 12px rgba(34,197,94,0.2)",
              "&:hover": {
                background: "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)",
                transform: "scale(1.02)",
              },
            }}
          >
            Confirm Accept
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={openDateHistoryModal}
        onClose={() => setOpenDateHistoryModal(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          p: 3,
          width: { xs: '95%', sm: '80%', md: '70%' },
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          fontSize: "0.8rem",
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} pb={1} sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: "1.1rem" }}>
              Date History
            </Typography>
            <IconButton onClick={() => setOpenDateHistoryModal(false)} size="small">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Paper sx={{ height: 300, width: "100%", boxShadow: 0, borderRadius: 0 }}>
            <DataGrid
              rows={
                selectedOrder?.deliveryDateHistories?.map((history, index) => ({
                  id: index,
                  oldDate: history.oldDeliveryDate,
                  newDate: history.newDeliveryDate,
                  reason: history.region
                })) || []
              }
              columns={[
                {
                  field: "serialNo",
                  headerName: "S.No.",
                  flex: 1,
                  sortable: false,
                  renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
                },
                { field: "oldDate", headerName: "Old Date", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----" },
                { field: "newDate", headerName: "New Date", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----" },
                { field: "reason", headerName: "Reason", flex: 2 },
              ]}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 5, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 15]}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={vendorOrder.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
                height: 300,
              }}
            />
          </Paper>
        </Box>
      </Modal>
      {/* 📌 Popup */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1, bgcolor: "#fafafa" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Custom Sizes
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {customSizes.length === 0 ? (
            <Typography color="text.secondary">No custom sizes found</Typography>
          ) : (customSizes.map((sizeData, idx) => (
            <Paper
              key={idx}
              elevation={2}
              sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "white" }}
            >
              <Typography variant="subtitle1" fontWeight="semibold" gutterBottom>
                Product: {sizeData?.product?.title}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {Object.entries(sizeData)
                  .filter(([key]) => !["id", "product", "createdAt"].includes(key)) // exclude unwanted keys
                  .map(([key, value]) =>
                    value !== null && value !== "" ? (   // ✅ null/empty string ko skip karo
                      <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                        <TextField
                          label={key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                          value={value}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled
                        />
                      </Grid>
                    ) : null
                  )}
              </Grid>
            </Paper>
          ))
          )}
        </DialogContent>
      </Dialog>
      {/* Common Popup */}
      <ImagePopup
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        imageUrl={imageUrl}
      />
    </div >
  );
};

export default OrdersWithCustomSize;
